import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Activity, 
  Users, 
  Eye, 
  Clock, 
  TrendingUp,
  Calendar,
  User,
  MousePointer,
  BarChart3,
  RefreshCw,
  ChevronRight,
  LogIn
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccessControlPage = () => {
  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userJourney, setUserJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/access-stats?days=${days}`),
        axios.get(`${API}/admin/access-logs?days=${days}&limit=100`)
      ]);
      setStats(statsRes.data);
      setRecentLogs(logsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos de accesos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserJourney = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/user-journey/${userId}?days=${days}`);
      setUserJourney(response.data);
      setSelectedUser(userId);
    } catch (error) {
      toast.error('Error al cargar recorrido del usuario');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '-';
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    return `Hace ${diffDays} días`;
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'login': return 'bg-green-500/20 text-green-400';
      case 'logout': return 'bg-red-500/20 text-red-400';
      case 'click': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  if (loading && !stats) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Control de Accesos</h1>
            <p className="text-muted-foreground">Monitorea quién accede y qué visitan tus usuarios</p>
          </div>
          <div className="flex items-center gap-2">
            <select 
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="bg-zinc-800 border-zinc-700 rounded-md px-3 py-2 text-sm"
            >
              <option value={1}>Hoy</option>
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
            </select>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Accesos</p>
                  <p className="text-2xl font-bold text-primary">{stats?.total_accesses || 0}</p>
                </div>
                <Activity className="h-8 w-8 text-primary/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Usuarios Únicos</p>
                  <p className="text-2xl font-bold text-green-400">{stats?.unique_users || 0}</p>
                </div>
                <Users className="h-8 w-8 text-green-400/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Páginas Visitadas</p>
                  <p className="text-2xl font-bold text-blue-400">{Object.keys(stats?.pages_visited || {}).length}</p>
                </div>
                <Eye className="h-8 w-8 text-blue-400/50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Logins Recientes</p>
                  <p className="text-2xl font-bold text-amber-400">{stats?.recent_logins?.length || 0}</p>
                </div>
                <LogIn className="h-8 w-8 text-amber-400/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="activity" className="space-y-4">
          <TabsList className="bg-zinc-800/50">
            <TabsTrigger value="activity">Actividad Reciente</TabsTrigger>
            <TabsTrigger value="users">Usuarios</TabsTrigger>
            <TabsTrigger value="pages">Páginas Populares</TabsTrigger>
            <TabsTrigger value="logins">Logins</TabsTrigger>
          </TabsList>

          {/* Actividad Reciente */}
          <TabsContent value="activity">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Actividad en Tiempo Real
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-2">
                    {recentLogs.map((log, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg hover:bg-zinc-800/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${log.user_role === 'admin' ? 'bg-primary/20' : 'bg-blue-500/20'}`}>
                            <User className={`h-5 w-5 ${log.user_role === 'admin' ? 'text-primary' : 'text-blue-400'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-foreground">{log.user_name}</span>
                              <Badge variant="outline" className="text-xs">
                                {log.user_role === 'admin' ? 'Admin' : 'Cliente'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>{log.page_title || log.page}</span>
                              <Badge className={`text-xs ${getActionColor(log.action)}`}>
                                {log.action}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">{formatTimeAgo(log.timestamp)}</p>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => fetchUserJourney(log.user_id)}
                            className="text-xs text-primary hover:text-primary"
                          >
                            Ver recorrido <ChevronRight className="h-3 w-3 ml-1" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {recentLogs.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">No hay actividad registrada</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Usuarios Más Activos */}
          <TabsContent value="users">
            <div className="grid lg:grid-cols-2 gap-4">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Usuarios Más Activos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {stats?.most_active_users?.map((user, index) => (
                        <div 
                          key={user.user_id}
                          className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg cursor-pointer hover:bg-zinc-800/50 transition-colors"
                          onClick={() => fetchUserJourney(user.user_id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium text-foreground">{user.name}</p>
                              <p className="text-xs text-muted-foreground">{user.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-primary">{user.total_visits}</p>
                            <p className="text-xs text-muted-foreground">visitas</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Detalle de Usuario Seleccionado */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MousePointer className="h-5 w-5 text-primary" />
                    Recorrido del Usuario
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {userJourney ? (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-4">
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="font-medium text-foreground">{userJourney.user?.name}</p>
                          <p className="text-sm text-muted-foreground">{userJourney.user?.email}</p>
                          <p className="text-xs text-primary mt-1">{userJourney.total_visits} acciones registradas</p>
                        </div>
                        
                        <div className="space-y-3">
                          {userJourney.sessions?.slice(0, 10).map((session, idx) => (
                            <div key={idx} className="border-l-2 border-primary/30 pl-4 space-y-2">
                              <p className="text-xs text-muted-foreground">
                                {formatTimestamp(session.start)}
                              </p>
                              {session.pages.map((page, pIdx) => (
                                <div key={pIdx} className="flex items-center gap-2 text-sm">
                                  <ChevronRight className="h-3 w-3 text-primary" />
                                  <span className="text-foreground">{page.title || page.page}</span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                      <p>Selecciona un usuario para ver su recorrido</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Páginas Populares */}
          <TabsContent value="pages">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  Páginas Más Visitadas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats?.top_pages?.map((page, index) => {
                    const maxCount = stats.top_pages[0]?.count || 1;
                    const percentage = (page.count / maxCount) * 100;
                    
                    return (
                      <div key={page.page} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-foreground">{page.title}</span>
                          <span className="text-sm font-bold text-primary">{page.count}</span>
                        </div>
                        <div className="w-full bg-zinc-800 rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logins Recientes */}
          <TabsContent value="logins">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <LogIn className="h-5 w-5 text-primary" />
                  Inicios de Sesión Recientes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {stats?.recent_logins?.map((login, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                            <LogIn className="h-5 w-5 text-green-400" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{login.user_name}</p>
                            <p className="text-xs text-muted-foreground">{login.user_email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-foreground">{formatTimestamp(login.timestamp)}</p>
                          <p className="text-xs text-muted-foreground">{formatTimeAgo(login.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                    {(!stats?.recent_logins || stats.recent_logins.length === 0) && (
                      <p className="text-center text-muted-foreground py-8">No hay logins registrados</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AccessControlPage;
